<script lang="ts">
	// @ts-nocheck
	import * as Carousel from '$lib/components/ui/carousel/index.js';
	import * as Dialog from '$lib/components/ui/dialog/index.js';
	import type { CarouselAPI } from '$lib/components/ui/carousel/context.js';
	import { enhancedImages } from '$lib/utils/images';
	import { onMount } from 'svelte';

	let { tour } = $props();
	let videos = [
		{
			productCode: 'P18ZC5', // Replace with your actual product code
			src: 'https://www.youtube.com/embed/hAkxkStVOQQ' // Replace with your actual YouTube video ID or embed URL
		},
		{
			productCode: 'PJHLE0', // Replace with your actual product code
			src: 'https://www.youtube.com/embed/sOVKn60RX9s' // Replace with your actual YouTube video ID or embed URL
		},
		{
			productCode: 'PQKW4C', // Replace with your actual product code
			src: 'https://www.youtube.com/embed/MqVeiwLNYYY' // Replace with your actual YouTube video ID or embed URL
		},
		{
			productCode: 'PNVP8M', // Replace with your actual product code
			src: 'https://www.youtube.com/embed/FRktDNIX8Mo' // Replace with your actual YouTube video ID or embed URL
		},
		{
			productCode: 'PTC0KJ', // Replace with your actual product code
			src: 'https://www.youtube.com/embed/5E2OmcTbHcs' // Replace with your actual YouTube video ID or embed URL
		},
		{
			productCode: 'PNNK81', // A Day At Wineglass Bay
			src: 'https://www.youtube.com/embed/rkdQ8bkixHE'
		},
		{
			productCode: 'PHJFAH', // A Day At Wineglass Bay - Launceston to Hobart
			src: 'https://www.youtube.com/embed/rkdQ8bkixHE'
		}
	];

	// Function to check if an image URL is in the enhancedImages collection
	function isEnhancedImage(imageUrl) {
		if (!imageUrl) return false;
		
		// Only consider local file paths as potential enhanced images
		// External URLs (containing http:// or https://) are not enhanced
		if (imageUrl.includes('http://') || imageUrl.includes('https://')) {
			return false;
		}
		
		// Try to find the image in our enhanced images collection
		try {
			return !!Object.entries(enhancedImages).find(([key]) => key === imageUrl);
		} catch (err) {
			console.warn('Error checking enhanced image:', err);
			return false;
		}
	}
	
	// Function to get the appropriate image source
	function getImageSource(imageUrl) {
		if (!imageUrl) return null;
		
		try {
			// If it's an enhanced image, get it from the enhancedImages collection
			const enhancedEntry = Object.entries(enhancedImages).find(([key]) => key === imageUrl);
			if (enhancedEntry && enhancedEntry[1] && enhancedEntry[1].default) {
				return enhancedEntry[1].default;
			}
		} catch (err) {
			console.warn('Error getting enhanced image source:', err);
		}
		
		// Otherwise, use the URL directly
		return imageUrl;
	}

	let api = $state<CarouselAPI>();
	const count = $derived(api ? api.scrollSnapList().length : 0);
	let current = $state(0);
	const hasVideo = $derived(videos.some((v) => v.productCode === tour.productCode));

	$effect(() => {
		if (api) {
			current = api.selectedScrollSnap() + 1;
			api.on('select', () => {
				current = api!.selectedScrollSnap() + 1;
			});
		}
	});

	// Lightbox state. `lightboxStart` seeds the lightbox carousel's initial slide
	// so it opens on whichever image the user clicked.
	let lightboxOpen = $state(false);
	let lightboxStart = $state(0);
	let lightboxApi = $state<CarouselAPI>();
	let lightboxCurrent = $state(0);

	$effect(() => {
		if (lightboxApi) {
			lightboxCurrent = lightboxApi.selectedScrollSnap();
			lightboxApi.on('select', () => {
				lightboxCurrent = lightboxApi!.selectedScrollSnap();
			});
		}
	});

	// Open the lightbox at a given image index (index into tour.images).
	function openLightbox(imageIndex) {
		lightboxStart = imageIndex;
		lightboxOpen = true;
	}

	// When the lightbox closes, keep the main gallery in sync with the last
	// image the user was viewing. `snapOffset` accounts for the leading video
	// slide in galleries that have one.
	function syncMainFromLightbox(snapOffset) {
		if (api) api.scrollTo(lightboxCurrent + snapOffset);
	}
	
	// Function to restore original image URLs after build
	function restoreOriginalImageUrls() {
		if (!tour || !tour.images) return;
		
		// Check each image for stored original URLs (from server-side)
		tour.images.forEach(img => {
			if (img._originalUrl) {
				// Restore the original URL that was replaced during build
				img.itemUrl = img._originalUrl;
				delete img._originalUrl;
			}
		});
	}
	
	// When component mounts, restore any original image URLs
	onMount(() => {
		restoreOriginalImageUrls();
	});
</script>

<section class="mx-auto flex w-full max-w-7xl flex-col px-4 pb-0 pt-20 sm:pb-12">
	<h2
		class="mt-10 scroll-m-20 pb-2 font-heading text-2xl font-semibold tracking-tight transition-colors first:mt-0 lg:text-3xl"
	>
		Gallery
	</h2>
	{#if videos.some((v) => v.productCode === tour.productCode)}
		<div
			class="mx-auto flex w-full max-w-7xl flex-col gap-4 overflow-hidden py-12 lg:grid lg:grid-cols-4"
		>
			<div class=" col-span-3 aspect-video">
				<Carousel.Root
					setApi={(emblaApi) => (api = emblaApi)}
					class="w-full rounded-3xl"
					opts={{ align: 'start', loop: true }}
				>
					<Carousel.Content class=" rounded-3xl lg:h-[480px]">
						<Carousel.Item class="aspect-video w-full lg:h-[480px]">
							<iframe
								src={videos.find((v) => v.productCode === tour.productCode)?.src}
								title="YouTube video player"
								class="h-full w-full rounded-3xl"
								frameborder="0"
								allowfullscreen
							></iframe>
						</Carousel.Item>
						{#each tour.images as image, index}
							<Carousel.Item class="aspect-video w-full lg:h-[480px]">
								<button
									type="button"
									aria-label="Open image in lightbox"
									onclick={() => openLightbox(index)}
									class="block h-full w-full cursor-zoom-in"
								>
									{#if isEnhancedImage(image.itemUrl)}
										<enhanced:img
											src={getImageSource(image.itemUrl)}
											alt={tour.name}
											class="h-full w-full rounded-3xl object-cover"
											sizes="(min-width: 1024px) 1024px, 100vw"
											loading="lazy"
										/>
									{:else}
										<img
											src={image.itemUrl}
											alt={tour.name || 'Tour Image'}
											class="h-full w-full rounded-3xl object-cover"
											loading="lazy"
											onerror={(e) => {
												console.warn('Gallery image failed to load:', image.itemUrl);
												e.currentTarget.onerror=null;
												e.currentTarget.src='/images/placeholder-tour.jpg';
											}}
										/>
									{/if}
								</button>
							</Carousel.Item>
						{/each}
					</Carousel.Content>
				</Carousel.Root>
			</div>

			<div class="relative col-span-1 flex w-full gap-4 overflow-auto lg:h-[480px] lg:flex-col">
				{#each tour.images as image, index}
					<button
						aria-label="gallery-select"
						onclick={() => api.scrollTo(index + 1)}
						class="relative w-full p-20 transition {current === index + 2
							? 'opacity-100'
							: 'opacity-60 hover:opacity-100'}"
					>
						<!-- Active-image indicator -->
						<span
							class="pointer-events-none absolute inset-y-0 left-1 z-10 my-auto h-1/3 w-1.5 rounded-full bg-primary transition-opacity {current ===
							index + 2
								? 'opacity-100'
								: 'opacity-0'}"
						></span>
						{#if isEnhancedImage(image.itemUrl)}
							<enhanced:img
								src={getImageSource(image.itemUrl)}
								class="absolute inset-0 h-full w-full rounded-xl object-cover lg:rounded-3xl"
								alt={tour.name}
								sizes="(min-width: 1024px) 25vw, 100px"
								loading="lazy"
							/>
						{:else}
							<img
								src={image.itemUrl}
								class="absolute inset-0 h-full w-full rounded-xl object-cover lg:rounded-3xl"
								alt={tour.name || 'Tour Image'}
								loading="lazy"
								onerror={(e) => { 
									console.warn('Gallery thumbnail failed to load:', image.itemUrl);
									e.currentTarget.onerror=null; 
									e.currentTarget.src='/images/placeholder-tour.jpg'; 
								}}
							/>
						{/if}
					</button>
				{/each}
				<button
					aria-label="gallery-select"
					onclick={() => api.scrollTo(0)}
					class="relative w-full p-20 transition {current === 1
						? 'opacity-100'
						: 'opacity-60 hover:opacity-100'}"
				>
					<!-- Active-image indicator -->
					<span
						class="pointer-events-none absolute inset-y-0 left-1 z-10 my-auto h-1/3 w-1.5 rounded-full bg-primary transition-opacity {current ===
						1
							? 'opacity-100'
							: 'opacity-0'}"
					></span><iframe
						src={videos.find((v) => v.productCode === tour.productCode)?.src}
						title="YouTube video player"
						class="pointer-events-none absolute inset-0 h-full w-full rounded-xl object-cover lg:rounded-3xl"
						frameborder="0"
						allowfullscreen
					></iframe></button
				>
			</div>
		</div>
	{:else}
		<div
			class="mx-auto flex w-full max-w-7xl flex-col gap-4 overflow-hidden py-12 lg:grid lg:grid-cols-4"
		>
			<div class=" col-span-3 aspect-video">
				<Carousel.Root
					setApi={(emblaApi) => (api = emblaApi)}
					class="w-full rounded-3xl"
					opts={{ align: 'start', loop: true }}
				>
					<Carousel.Content class=" rounded-3xl lg:h-[480px]">
						{#each tour.images as image, index}
							<Carousel.Item class="aspect-video w-full lg:h-[480px]">
								<button
									type="button"
									aria-label="Open image in lightbox"
									onclick={() => openLightbox(index)}
									class="block h-full w-full cursor-zoom-in"
								>
									{#if isEnhancedImage(image.itemUrl)}
										<enhanced:img
											src={getImageSource(image.itemUrl)}
											alt={tour.name}
											class="h-full w-full rounded-3xl object-cover"
											sizes="(min-width: 1024px) 1024px, 100vw"
											loading="lazy"
										/>
									{:else}
										<img
											src={image.itemUrl}
											alt={tour.name || 'Tour Image'}
											class="h-full w-full rounded-3xl object-cover"
											loading="lazy"
											onerror={(e) => {
												console.warn('Gallery image failed to load:', image.itemUrl);
												e.currentTarget.onerror=null;
												e.currentTarget.src='/images/placeholder-tour.jpg';
											}}
										/>
									{/if}
								</button>
							</Carousel.Item>
						{/each}
					</Carousel.Content>
				</Carousel.Root>
			</div>
			<div class="relative col-span-1 flex w-full gap-4 overflow-auto lg:h-[480px] lg:flex-col">
				{#each tour.images as image, index}
					<button
						aria-label="gallery-select"
						onclick={() => api.scrollTo(index)}
						class="relative w-full p-20 transition {current === index + 1
							? 'opacity-100'
							: 'opacity-60 hover:opacity-100'}"
					>
						<!-- Active-image indicator -->
						<span
							class="pointer-events-none absolute inset-y-0 left-1 z-10 my-auto h-1/3 w-1.5 rounded-full bg-primary transition-opacity {current ===
							index + 1
								? 'opacity-100'
								: 'opacity-0'}"
						></span>
						{#if isEnhancedImage(image.itemUrl)}
							<enhanced:img
								src={getImageSource(image.itemUrl)}
								class="absolute inset-0 h-full w-full rounded-xl object-cover lg:rounded-3xl"
								alt={tour.name}
								sizes="(min-width: 1024px) 25vw, 100px"
								loading="lazy"
							/>
						{:else}
							<img
								src={image.itemUrl}
								class="absolute inset-0 h-full w-full rounded-xl object-cover lg:rounded-3xl"
								alt={tour.name || 'Tour Image'}
								loading="lazy"
								onerror={(e) => { 
									console.warn('Gallery thumbnail failed to load:', image.itemUrl);
									e.currentTarget.onerror=null; 
									e.currentTarget.src='/images/placeholder-tour.jpg'; 
								}}
							/>
						{/if}
					</button>
				{/each}
			</div>
		</div>
	{/if}

	<!-- Lightbox: opens when the active gallery image is clicked -->
	<Dialog.Root
		bind:open={lightboxOpen}
		onOpenChange={(open) => {
			if (!open) syncMainFromLightbox(hasVideo ? 1 : 0);
		}}
	>
		<Dialog.Content
			class="max-w-[95vw] border-none bg-transparent p-0 shadow-none sm:max-w-5xl"
		>
			<Dialog.Title class="sr-only">{tour.name} gallery</Dialog.Title>
			<Dialog.Description class="sr-only">
				Browse through the gallery images for {tour.name}.
			</Dialog.Description>
			{#if lightboxOpen}
				<Carousel.Root
					setApi={(emblaApi) => (lightboxApi = emblaApi)}
					class="w-full"
					opts={{ align: 'start', loop: true, startIndex: lightboxStart }}
				>
					<Carousel.Content>
						{#each tour.images as image, index}
							<Carousel.Item class="flex aspect-video w-full items-center justify-center">
								{#if isEnhancedImage(image.itemUrl)}
									<enhanced:img
										src={getImageSource(image.itemUrl)}
										alt={tour.name}
										class="max-h-[85vh] w-full rounded-2xl object-contain"
										sizes="95vw"
									/>
								{:else}
									<img
										src={image.itemUrl}
										alt={tour.name || 'Tour Image'}
										class="max-h-[85vh] w-full rounded-2xl object-contain"
										onerror={(e) => {
											console.warn('Lightbox image failed to load:', image.itemUrl);
											e.currentTarget.onerror=null;
											e.currentTarget.src='/images/placeholder-tour.jpg';
										}}
									/>
								{/if}
							</Carousel.Item>
						{/each}
					</Carousel.Content>
					<Carousel.Previous
						class="left-2 top-1/2 h-10 w-10 -translate-y-1/2 sm:-left-12"
					/>
					<Carousel.Next
						class="right-2 top-1/2 h-10 w-10 -translate-y-1/2 sm:-right-12"
					/>
				</Carousel.Root>
				<div
					class="pointer-events-none absolute bottom-4 left-1/2 z-10 -translate-x-1/2 rounded-full bg-background/80 px-3 py-1 text-sm font-medium text-foreground"
				>
					{lightboxCurrent + 1} / {tour.images.length}
				</div>
			{/if}
		</Dialog.Content>
	</Dialog.Root>
</section>
